import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import SampleSetLayout from '../../../../../pages/experiments/SampleSetLayout';
import { sampleSetQueryOptions } from '../../../../../services/queries';
import {
  VIEW_EXPERIMENTS_PAGE,
  VIEW_EXPERIMENTS_V2_PAGE,
} from '../../../../../utils/permissions';
import { MS_LEVELS } from '../../../../../enums';

const legacyTabRoutes = {
  0: '/experiments/$sampleSetId/samples',
  1: '/experiments/$sampleSetId/metadata',
  2: '/experiments/$sampleSetId/analytes',
  3: '/experiments/$sampleSetId/data-qc',
  4: '/experiments/$sampleSetId/differential-analysis',
} as const;

// Sample-list state shared by the Samples and Metadata tabs. Defaults live
// here as the single source of truth, so `useSearch` always returns concrete
// values and the UI components never need their own fallbacks.
const searchSchema = z.object({
  // Legacy redirect param — keep until ?tab= hits go to 0 in RUM
  tab: z.number().optional(),
  sampleSearch: z.string().catch('').default(''),
  page: z.number().int().positive().catch(1).default(1),
  rowsPerPage: z.number().int().positive().catch(50).default(50),
  orderBy: z
    .enum(['created_date', 'sample_name', 'polarity'])
    .catch('created_date')
    .default('created_date'),
  orderDirection: z.enum(['asc', 'desc']).catch('asc').default('asc'),
  statusFilter: z.array(z.string()).catch([]).default([]),
  modeFilter: z.array(z.string()).catch([]).default([]),
  msLevelFilter: z.array(z.enum(MS_LEVELS)).catch([]).default([]),
  metadataFilters: z
    .record(z.string(), z.array(z.string()))
    .catch({})
    .default({}),
  createdFrom: z.iso
    .datetime({ offset: true })
    .nullable()
    .catch(null)
    .default(null),
  createdTo: z.iso
    .datetime({ offset: true })
    .nullable()
    .catch(null)
    .default(null),
  selectedJobId: z.string().nullable().catch(null).default(null),
});

export const Route = createFileRoute(
  '/_authenticated/_navbar/experiments/$sampleSetId'
)({
  validateSearch: searchSchema,
  staticData: { ddViewName: 'experiments-page-details' },
  beforeLoad: ({ context, search, params }) => {
    if (
      !context.auth.hasPermission(VIEW_EXPERIMENTS_PAGE) &&
      !context.auth.hasPermission(VIEW_EXPERIMENTS_V2_PAGE)
    ) {
      throw notFound();
    }

    // Legacy `?tab=N` URL redirect — older deep links
    if (search.tab) {
      const targetPath =
        legacyTabRoutes[search.tab as keyof typeof legacyTabRoutes];
      if (targetPath) {
        throw redirect({
          to: targetPath,
          params: { sampleSetId: params.sampleSetId },
          replace: true,
        });
      }
    }
  },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(
        sampleSetQueryOptions(params.sampleSetId, context.auth.hasPermission)
      );
      return { sampleSetId: params.sampleSetId };
    } catch {
      throw notFound();
    }
  },
  component: SampleSetLayout,
});
